import React from 'react';
import { Paper, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { hasQuartileDataForYear } from '../../utils/quartiles';
import { glassTooltipContentStyle, glassTooltipItemStyle, glassTooltipLabelStyle } from '../../styles/chartTooltip';

const COLORS = { male: 'rgba(0, 86, 255, 0.95)', female: 'rgba(255, 71, 57, 0.95)' };

const parsePercent = (value: any) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed / 100 : null;
};

const QuartileDistributionChart: React.FC<{ company: any; selectedYear?: number | null }> = ({ company, selectedYear }) => {
  const availableYears = Object.keys(company)
    .map((key) => {
      const match = key.match(/^(\d{4})_MaleTopQuartile$/);
      if (!match) return null;
      return Number(match[1]);
    })
    .filter((year): year is number => year !== null)
    .filter((year) => hasQuartileDataForYear(company, year))
    .sort((a, b) => b - a);

  const recentYear = availableYears.length > 0 ? availableYears[0] : null;
  const activeYear = selectedYear ?? recentYear;

  const quartileData = activeYear ? [
    { name: 'Top', Male: parsePercent(company[`${activeYear}_MaleTopQuartile`]), Female: parsePercent(company[`${activeYear}_FemaleTopQuartile`]) },
    { name: 'Upper Mid', Male: parsePercent(company[`${activeYear}_MaleUpperMiddleQuartile`]), Female: parsePercent(company[`${activeYear}_FemaleUpperMiddleQuartile`]) },
    { name: 'Lower Mid', Male: parsePercent(company[`${activeYear}_MaleLowerMiddleQuartile`]), Female: parsePercent(company[`${activeYear}_FemaleLowerMiddleQuartile`]) },
    { name: 'Lower', Male: parsePercent(company[`${activeYear}_MaleLowerQuartile`]), Female: parsePercent(company[`${activeYear}_FemaleLowerQuartile`]) },
  ].filter(q => (q.Male !== null && q.Male !== undefined) || (q.Female !== null && q.Female !== undefined)) : [];

  if (!activeYear) {
    return null;
  }

  if (quartileData.length === 0) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6" align="center" gutterBottom>Gender Distribution by Pay Quartile ({activeYear})</Typography>
        <Typography align="center" sx={{ mt: 6, color: 'text.secondary' }}>
          Quartile distribution data is not available for this year.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" align="center" gutterBottom>Gender Distribution by Pay Quartile ({activeYear})</Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={quartileData} layout="vertical" stackOffset="expand">
          <CartesianGrid stroke="#e5e7eb" strokeWidth={0.8} vertical={true} horizontal={false} />
          <XAxis type="number" tickFormatter={(tick) => `${tick * 100}%`} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={80} axisLine={false} tickLine={false} />
          <Tooltip
            formatter={(value: number | undefined) => value === undefined ? 'N/A' : `${(value * 100).toFixed(1)}%`}
            contentStyle={glassTooltipContentStyle}
            labelStyle={glassTooltipLabelStyle}
            itemStyle={glassTooltipItemStyle}
          />
          <Legend
            align="center"
            verticalAlign="top"
            wrapperStyle={{ left: 44, right: 0 }}
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;
              const ordered = ['Male', 'Female']
                .map((label) => payload.find((entry) => entry.value === label))
                .filter(Boolean) as typeof payload;
              return (
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    gap: 16,
                    justifyContent: 'center'
                  }}
                >
                  {ordered.map((entry) => (
                    <li key={entry.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: entry.color,
                          display: 'inline-block',
                          borderRadius: 2
                        }}
                      />
                      <span style={{ color: entry.color, fontSize: 16, fontWeight: 600 }}>{entry.value}</span>
                    </li>
                  ))}
                </ul>
              );
            }}
          />
          <Bar dataKey="Male" name="Male" stackId="a" fill={COLORS.male} radius={[4, 1, 1, 4]} />
          <Bar dataKey="Female" name="Female" stackId="a" fill={COLORS.female} radius={[1, 4, 4, 1]} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default QuartileDistributionChart;
